import { useCallback, useEffect, useState } from 'react';
import { useSessionNote, useSaveSessionNote, useDeleteSessionNote } from '@theraply/api-client';
import { encryptNote, decryptNote } from '@theraply/crypto';
import { base64ToBytes } from '@theraply/crypto';

/**
 * Combined hook that handles transparent encryption/decryption of session notes.
 * Uses the therapist's master key from session storage.
 */

interface UseEncryptedNoteResult {
  plaintext: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  saveNote: (content: string) => Promise<void>;
  deleteNote: () => Promise<void>;
  hasNote: boolean;
}

/**
 * Get the therapist's master key from session storage
 */
function getMasterKey(): Uint8Array | null {
  const masterKeyBase64 = sessionStorage.getItem('therapist_master_key');
  if (!masterKeyBase64) {
    return null;
  }
  try {
    return base64ToBytes(masterKeyBase64);
  } catch (error) {
    console.error('Failed to decode master key:', error);
    return null;
  }
}

/**
 * Hook for encrypted session notes with automatic encryption/decryption
 */
export function useEncryptedNote(bookingId: number): UseEncryptedNoteResult {
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [decryptError, setDecryptError] = useState<Error | null>(null);

  // Fetch encrypted note
  const {
    data: encryptedNote,
    isLoading,
    error: fetchError,
  } = useSessionNote(bookingId, { enabled: !!bookingId });

  // Save mutation
  const saveNoteMutation = useSaveSessionNote();
  const deleteNoteMutation = useDeleteSessionNote();

  // Decrypt note when it's loaded
  useEffect(() => {
    if (!encryptedNote) {
      setPlaintext(null);
      setDecryptError(null);
      return;
    }

    const masterKey = getMasterKey();
    if (!masterKey) {
      setDecryptError(new Error('Master key not found. Please log in again.'));
      return;
    }

    // Decrypt asynchronously
    const decrypt = async () => {
      try {
        const decrypted = await decryptNote(
          {
            ciphertext: encryptedNote.encryptedContent,
            iv: encryptedNote.iv,
            version: encryptedNote.version,
          },
          masterKey
        );
        setPlaintext(decrypted);
        setDecryptError(null);
      } catch (error) {
        console.error('Failed to decrypt note:', error);
        setDecryptError(error instanceof Error ? error : new Error('Decryption failed'));
        setPlaintext(null);
      }
    };

    decrypt();
  }, [encryptedNote]);

  // Save note (encrypt then save)
  const saveNote = useCallback(
    async (content: string) => {
      const masterKey = getMasterKey();
      if (!masterKey) {
        throw new Error('Master key not found. Please log in again.');
      }

      // Encrypt the content
      const encrypted = await encryptNote(content, masterKey);

      // Save to server
      await saveNoteMutation.mutateAsync({
        bookingId,
        data: {
          encryptedContent: encrypted.ciphertext,
          iv: encrypted.iv,
          version: encrypted.version,
        },
      });

      // Update local state immediately
      setPlaintext(content);
    },
    [bookingId, saveNoteMutation]
  );

  // Delete note
  const deleteNote = useCallback(async () => {
    await deleteNoteMutation.mutateAsync(bookingId);
    setPlaintext(null);
  }, [bookingId, deleteNoteMutation]);

  // Combine errors
  const error = decryptError || (fetchError as Error | null) || saveNoteMutation.error || deleteNoteMutation.error;

  return {
    plaintext,
    isLoading,
    isSaving: saveNoteMutation.isPending || deleteNoteMutation.isPending,
    error,
    saveNote,
    deleteNote,
    hasNote: !!encryptedNote,
  };
}

/**
 * Hook for saving notes with auto-save and debouncing
 */
export function useAutoSaveNote(bookingId: number, debounceMs: number = 5000) {
  const { plaintext, saveNote, isSaving, error } = useEncryptedNote(bookingId);
  const [localContent, setLocalContent] = useState(plaintext || '');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);

  // Update local content when plaintext changes from server
  useEffect(() => {
    if (plaintext !== null && plaintext !== localContent) {
      setLocalContent(plaintext);
    }
  }, [plaintext]);

  // Auto-save when content changes
  useEffect(() => {
    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Don't save if content hasn't changed or is empty
    if (localContent === plaintext || localContent.trim() === '') {
      return;
    }

    // Set new timeout for auto-save
    const timeout = setTimeout(() => {
      saveNote(localContent)
        .then(() => {
          setLastSaved(new Date());
        })
        .catch((err) => {
          console.error('Auto-save failed:', err);
        });
    }, debounceMs);

    setSaveTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [localContent, debounceMs]);

  // Manual save
  const manualSave = useCallback(async () => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    await saveNote(localContent);
    setLastSaved(new Date());
  }, [localContent, saveNote, saveTimeout]);

  return {
    content: localContent,
    setContent: setLocalContent,
    isSaving,
    error,
    lastSaved,
    manualSave,
  };
}
